import '../GraphSidePanel.css';

import type { FormikProps } from 'formik';
import { record } from 'fp-ts';
import { type FC, type ReactNode, useMemo } from 'react';

import {
  type DeviceGroupAttribute,
  type GraphPath,
  IPF_NETWORK_VIEWER,
  IPF_NETWORK_VIEWER_LABEL,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { getAvailableDeviceGroupAttributes } from '@ipfabric/utils/graphSettings/attributes';

import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { FormikCheckboxListField } from 'components/FormikCheckboxListField/FormikCheckboxListField';
import { FormikErrorMessage } from 'components/FormikErrorMessage/FormikErrorMessage';
import { FormikSelectFieldLegacy } from 'components/FormikSelectFieldLegacy/FormikSelectFieldLegacy';
import {
  formikSelectFieldRoundedControlStyle,
  formikSelectFieldRoundedMenuStyle,
} from 'components/FormikSelectFieldLegacy/styles';
import { FormikTextFieldLegacy } from 'components/FormikTextFieldLegacy/FormikTextFieldLegacy';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { selectTopologyMetadata } from 'components/Graph/model/selectors/graphMetadata/selectTopologyMetadata';
import {
  ScrollPaginator,
  type ScrollPaginatorProps,
} from 'components/ScrollPaginator/ScrollPaginator';
import { useLocale } from 'containers/App/LocaleContext';
import { useEffectOnChange } from 'utils/react/useEffectOnChange';
import { SearchIcon } from '../icons/SearchIcon';
import { addLayoutIconsToPathOptions } from './addLayoutIconsToPathOptions';

export interface PathOptionType {
  label: ReactNode;
  value: GraphPath;
}

interface Props {
  formik: FormikProps<GraphNetworkViewerDataInputFormValues>;
  loadingState: LoadingState;
  onGroupByChange: (value: DeviceGroupAttribute) => void;
  onLayoutEditClick: (path: GraphPath) => void;
  paths: PathOptionType[];
  pathsScrollPaginatorProps: ScrollPaginatorProps;
}

export interface GraphNetworkViewerDataInputFormValues {
  groupBy: DeviceGroupAttribute | null;
  paths: Set<GraphPath>;
  pathFilter: string;
}

export const GraphNetworkViewerDataInputForm: FC<Props> = (props) => {
  const msg = useLocale();

  const ctxProps = useGraphContextSelectors({
    pathLayouts: props.formik.values.groupBy
      ? prop('pathLayouts', props.formik.values.groupBy)
      : () => record.empty,
    topologyMetadata: selectTopologyMetadata,
  });

  const groupByOptions = useMemo(
    () =>
      getAvailableDeviceGroupAttributes({
        labelsEnabled: isFeatureFlagEnabled('ENABLE_LABELS'),
      }).map((value) => ({
        label:
          msg.canvasGraph.graphSidePanel.networkViewerDataInput.groupBy.options[
            value
          ],
        value,
      })),
    [msg],
  );

  const groupByHtmlId = useHtmlId('groupBy');

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.onLayoutEditClick): considered stable
  const pathOptions = useMemo(
    () =>
      addLayoutIconsToPathOptions(
        props.paths,
        ctxProps.pathLayouts,
        msg,
        props.onLayoutEditClick,
        ctxProps.topologyMetadata,
      ),
    [props.paths, ctxProps.pathLayouts, msg, ctxProps.topologyMetadata],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.onLayoutEditClick): considered stable
  const allNetworkOptions = useMemo(() => {
    const options = (
      props.formik.values.groupBy !== null && props.paths.length > 0
        ? [
            {
              label: IPF_NETWORK_VIEWER_LABEL,
              value: IPF_NETWORK_VIEWER,
            },
          ]
        : []
    ) as PathOptionType[];
    return addLayoutIconsToPathOptions(
      options,
      ctxProps.pathLayouts,
      msg,
      props.onLayoutEditClick,
      ctxProps.topologyMetadata,
    );
  }, [
    props.formik.values.groupBy,
    props.paths.length,
    ctxProps.pathLayouts,
    ctxProps.topologyMetadata,
    msg,
  ]);

  const getNoGroupsMessage = (): ReactNode => {
    if (props.formik.values.groupBy === null) {
      return msg.canvasGraph.graphSidePanel.networkViewerDataInput.pathList
        .noGroupBy;
    }
    if (props.formik.values.pathFilter) {
      return msg.canvasGraph.graphSidePanel.networkViewerDataInput.pathList
        .noFilteredPaths;
    }
    return msg.canvasGraph.graphSidePanel.networkViewerDataInput.pathList
      .noPaths;
  };

  useEffectOnChange(() => {
    props.formik.setFieldTouched('paths', false);
  }, [props.formik.values.groupBy]);

  const pathListId = useHtmlId('pathList');

  const childPathsSize = props.formik.values.paths.has(IPF_NETWORK_VIEWER)
    ? props.formik.values.paths.size - 1
    : props.formik.values.paths.size;
  const allChildPathsChecked = childPathsSize === props.paths.length;

  return (
    <div className='flex flex-1 flex-col p-5 pt-6'>
      <label htmlFor={groupByHtmlId} className='ipf-form-label'>
        {isFeatureFlagEnabled('ENABLE_LABELS')
          ? 'Group Devices by Label'
          : msg.canvasGraph.graphSidePanel.networkViewerDataInput.groupBy.label}
      </label>
      <FormikSelectFieldLegacy
        options={groupByOptions}
        formik={props.formik}
        id={groupByHtmlId}
        name='groupBy'
        onValueChange={props.onGroupByChange}
        placeholder={
          msg.canvasGraph.graphSidePanel.networkViewerDataInput.groupBy
            .placeholder
        }
        styles={{
          control: (base) => ({
            ...base,
            ...formikSelectFieldRoundedControlStyle,
          }),
          menu: (base) => ({
            ...base,
            ...formikSelectFieldRoundedMenuStyle,
          }),
        }}
      />
      <FormikErrorMessage formik={props.formik} name='groupBy' />

      <label className='ipf-form-label mt-lgu-3' htmlFor={pathListId}>
        {msg.canvasGraph.graphSidePanel.networkViewerDataInput.pathList.label}
      </label>
      <FormikTextFieldLegacy
        id={pathListId}
        autoTrim='left'
        addonBefore={
          <SearchIcon
            title={null}
            className='ipf-input-search-icon'
            width={16}
            height={16}
          />
        }
        className='ipf-input ipf-input--search w-full'
        formik={props.formik}
        name='pathFilter'
        placeholder={
          msg.canvasGraph.graphSidePanel.networkViewerDataInput.pathList
            .filterPlaceholder
        }
        // z-0 prevents the select dropdown above underlay this text input,
        // because input with addons use higher z-indexes inside
        wrapperClassName='relative z-0'
      />
      <div className='mt-5 flex flex-row justify-between font-semibold'>
        <span className='ipf-form-label'>
          {
            msg.canvasGraph.graphSidePanel.networkViewerDataInput.pathList
              .numberOfSelected
          }{' '}
          {props.formik.values.paths.size}
        </span>
      </div>
      {props.loadingState !== 'LOADING' &&
        props.loadingState !== 'ERROR' &&
        props.paths.length === 0 && (
          <p className='text-muted mt-2.5 text-center'>
            {getNoGroupsMessage()}
          </p>
        )}
      <div className='-mx-5 mt-2.5 flex flex-1 flex-col'>
        <FormikCheckboxListField
          disabled={allChildPathsChecked}
          formik={props.formik}
          name='paths'
          options={allNetworkOptions}
          optionClassName='graph-item-hr font-semibold'
        />
        <ScrollPaginator {...props.pathsScrollPaginatorProps}>
          <FormikCheckboxListField
            formik={props.formik}
            name='paths'
            options={pathOptions}
            optionClassName='pl-10'
          />
        </ScrollPaginator>
      </div>
      <FormikErrorMessage formik={props.formik} name='paths' />
    </div>
  );
};
